import React, { useEffect, useRef, useState } from "react";

// ─── Pointr SDK CDN links ─────────────────────────────────────────────────────
const POINTR_CSS = "https://pointr-websdk.azureedge.net/9.2.0/css/pointr.css";
const POINTR_JS = "https://pointr-websdk.azureedge.net/9.2.0/pointrwebsdk.js";

// ─── Demo credentials (Pointr playground) ────────────────────────────────────
// Swap these for real airport credentials when ready
const POINTR_OPTIONS = {
    apiUrl: "https://sample-app-v9.pointr.cloud",
    clientInternalIdentifier: "1ee58e56-fc35-4ec4-b32e-5d49e65fb52d",
    clientSecret: "af5d9431-56a3-46a9-ae0a-65d9757528d1",
    siteExternalIdentifier: "department-store",
};

// ─── Dummy POI IDs (two fixed locations from the Pointr playground) ───────────
// Replace with real airport POI IDs when credentials are swapped
// const START_POI_ID = "20cfd572-9a66-401d-aa3c-b10fe20cd45d";
// const DESTINATION_POI_ID = "efd87eb5-afe0-4262-9f3a-acfa4380e413";

// ─── Timing constants ─────────────────────────────────────────────────────────
const LOGO_DURATION = 4000; // ms logo shown while SDK initialises

// ─── Zoom level ───────────────────────────────────────────────────────────────
// Default playground zoom is ~20.25 which is too zoomed in for large/4K screens.
// 17 shows the full route comfortably. Increase to zoom in, decrease to zoom out.
const NAV_ZOOM_LEVEL = 17;

// ─── Per-step durations in ms ─────────────────────────────────────────────────
// Each entry = how long that step stays on screen before auto-advancing.
// Wire from backend later; replace durations as needed.
const DEFAULT_STEP_TIMINGS = [
    { stepNumber: 1, duration: 4000 },
    { stepNumber: 2, duration: 5000 },
    { stepNumber: 3, duration: 5000 },
    { stepNumber: 4, duration: 5000 },
    { stepNumber: 5, duration: 5000 },
    { stepNumber: 6, duration: 5000 },
    { stepNumber: 7, duration: 5000 },
    { stepNumber: 8, duration: 3000 },
];

// ─── Persistent Pointr container ────────────────────────────────────────────
// The Pointr SDK singleton renders INTO this div. We keep it alive at module
// level so React remounts (mapKey changes) never destroy the SDK's DOM.
let _pointrContainerEl = null;
const getPointrContainer = () => {
    if (!_pointrContainerEl) {
        _pointrContainerEl = document.createElement("div");
        _pointrContainerEl.id = "pointr-map-container";
        _pointrContainerEl.style.cssText = "width:100%;height:100%;";
    }
    return _pointrContainerEl;
};

// ─────────────────────────────────────────────────────────────────────────────
const BostonMap = ({
    isVisible = false,
    stepTimings = DEFAULT_STEP_TIMINGS,
    START_POI_ID = null,
    DESTINATION_POI_ID = null,
    key, // Add key prop support for remounting
    logoBeforeMap, 

    // Future props: startPoiId, destinationPoiId, siteExternalIdentifier, ...
}) => {
    const mapSlotRef = useRef(null); // React-managed slot div — we append the persistent container into it
    const pointrWebRef = useRef(null);
    const mapVCRef = useRef(null); // mapViewController — kept for zoom calls
    const navCtrlRef = useRef(null); // navigationViewController
    const sdkReadyRef = useRef(false);
    const navStartedRef = useRef(false);
    const stepTimerRef = useRef(null);

    const [phase, setPhase] = useState("logo"); // "logo" | "map"
    const [sdkError, setSdkError] = useState(null);

    // ── 1. Re-attach persistent Pointr container + resize on every mount ───────
    useEffect(() => {
        const slot = mapSlotRef.current;
        const container = getPointrContainer();
        if (slot && !slot.contains(container)) {
            slot.appendChild(container);
        }

        // Always resize after mount — moving the canvas to a new parent OR React
        // re-rendering can cause MapLibre to lose its dimensions and show grey.
        const triggerResize = () => {
            try {
                const pw = window.PointrWebSDK?.MapWidget?.getInstance?.();
                if (pw) {
                    const mapVC = pw.getUiController().getMapViewController();
                    if (mapVC?.resize) mapVC.resize();
                    const mapView = mapVC?.getView?.();
                    if (mapView?.resize) mapView.resize();
                }
            } catch (_) { }
        };

        const t1 = setTimeout(triggerResize, 100);
        const t2 = setTimeout(triggerResize, 500); // second pass for slower layouts
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    // ── Resize again the moment the map slot becomes visible (phase → "map") ──
    useEffect(() => {
        if (phase !== "map") return;
        const t = setTimeout(() => {
            try {
                const pw = window.PointrWebSDK?.MapWidget?.getInstance?.();
                if (pw) {
                    const mapVC = pw.getUiController().getMapViewController();
                    if (mapVC?.resize) mapVC.resize();
                }
            } catch (_) { }
        }, 50);
        return () => clearTimeout(t);
    }, [phase]);

    // ── 2. Inject Pointr CSS + JS once on page load ───────────────────────────
    useEffect(() => {
        // CSS
        if (!document.querySelector(`link[href="${POINTR_CSS}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = POINTR_CSS;
            document.head.appendChild(link);
        }

        // JS
        const existing = document.querySelector(`script[src="${POINTR_JS}"]`);
        if (existing) {
            if (window.PointrWebSDK) initPointr();
            return;
        }

        const script = document.createElement("script");
        script.src = POINTR_JS;
        script.async = true;
        script.onload = () => initPointr();
        script.onerror = () => setSdkError("Failed to load Pointr SDK");
        document.body.appendChild(script);

        // No cleanup — MapWidget is a singleton; stopping on unmount breaks re-mounts
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── 2. Init Pointr SDK ────────────────────────────────────────────────────
    const initPointr = () => {
        if (pointrWebRef.current) return;

        try {
            let pointrWeb;
            let isExistingInstance = false;

            // getInstance() returns undefined (not throw) when no instance exists
            try { pointrWeb = window.PointrWebSDK.MapWidget.getInstance(); } catch (_) { }

            if (pointrWeb) {
                // ── REPLAY PATH ─────────────────────────────────────────────────────
                // Singleton already exists → mapReady already fired, won't fire again.
                // Mark SDK as ready immediately so isVisible effect can call startNavigation.
                console.log("[Pointr] Reusing existing MapWidget instance");
                isExistingInstance = true;
                sdkReadyRef.current = true;
            } else {
                // ── FIRST LOAD PATH ──────────────────────────────────────────────────
                const options = new window.PointrWebSDK.Options({
                    container: "pointr-map-container",
                    ...POINTR_OPTIONS,
                });
                pointrWeb = new window.PointrWebSDK.MapWidget(options);
                pointrWeb.start();

                // Hide Pointr's built-in navigation panel — it covers the map UI
                if (!document.getElementById("pointr-nav-hide-style")) {
                    const hideNavStyle = document.createElement("style");
                    hideNavStyle.id = "pointr-nav-hide-style";
                    hideNavStyle.textContent = `#pointr-navigation { display: none !important; }`;
                    document.head.appendChild(hideNavStyle);
                }

                console.log("[Pointr] New MapWidget instance created");
            }

            pointrWebRef.current = pointrWeb;

            const mapViewController = pointrWeb.getUiController().getMapViewController();
            const mapView = mapViewController.getView();
            const navigationViewController = pointrWeb.getUiController().getNavigationViewController();

            mapVCRef.current = mapViewController;
            navCtrlRef.current = navigationViewController;

            if (isExistingInstance) {
                // mapReady already fired — if panel is visible, start nav right now
                if (isVisible && !navStartedRef.current) {
                    startNavigation(pointrWeb, navigationViewController, mapViewController);
                }
            } else {
                // Wait for mapReady on a fresh instance
                mapView.on(mapView.events.mapReady, async () => {
                    sdkReadyRef.current = true;
                    console.log("[Pointr] Map ready");

                    if (isVisible && !navStartedRef.current) {
                        await startNavigation(pointrWeb, navigationViewController, mapViewController);
                    }
                });
            }

        } catch (err) {
            console.error("[Pointr] init error:", err);
            setSdkError(err.message);
        }
    };

    // ── 3. Start navigation + zoom fix + auto-advance ─────────────────────────
    // ── Helper: wait until getCurrentSite() returns a valid site with POI data ─
    // mapReady fires before site POI data is fully loaded, so we retry/poll.
    const getSiteWithRetry = async (pointrWeb, maxRetries = 20, delayMs = 500) => {
        for (let i = 0; i < maxRetries; i++) {
            try {
                const site = pointrWeb.getCurrentSite();
                if (site && site.siteInternalIdentifier) {
                    console.log(`[Pointr] Site ready after ${i * delayMs}ms — id: ${site.siteInternalIdentifier}`);
                    return site;
                }
            } catch (_) { }
            await new Promise((r) => setTimeout(r, delayMs));
        }
        console.warn("[Pointr] getSiteWithRetry: site never became ready");
        return null;
    };

    const startNavigation = async (pointrWeb, navigationViewController, mapViewController) => {
        if (navStartedRef.current) return;
        navStartedRef.current = true;

        try {
            // Wait until site has a valid internalIdentifier (POI data loaded)
            const mySite = await getSiteWithRetry(pointrWeb);
            if (!mySite) {
                console.error("[Pointr] Could not get a valid site — aborting navigation");
                navStartedRef.current = false;
                return;
            }
            console.log("[Pointr] Got site:", mySite.siteInternalIdentifier);

            const myStartPoi = await pointrWeb.getPoiManager().get(START_POI_ID, mySite);
            const myDestinationPoi = await pointrWeb.getPoiManager().get(DESTINATION_POI_ID, mySite);

            console.log("[Pointr] Start POI:", myStartPoi?.name ?? "NOT FOUND");
            console.log("[Pointr] Dest  POI:", myDestinationPoi?.name ?? "NOT FOUND");

            if (!myStartPoi) { console.error("[Pointr] Start POI not found"); navStartedRef.current = false; return; }
            if (!myDestinationPoi) { console.error("[Pointr] Destination POI not found"); navStartedRef.current = false; return; }

            pointrWeb.highlight(myStartPoi);
            pointrWeb.highlight(myDestinationPoi);
            pointrWeb.hidePoiDetails();

            // populate first, then settle 200ms before startNavigation so Pointr
            // has time to compute the route before switching to nav view
            navigationViewController.populate(myStartPoi, myDestinationPoi);
            await new Promise((r) => setTimeout(r, 200));
            navigationViewController.startNavigation();
            console.log("[Pointr] Navigation started ✅");

            // ── FIX: Zoom out so full route + cursor stays in view ───────────────
            // Default zoom (~20.25) is too tight on large/4K screens.
            // fitBoundsForRoute() fits the full path, then we nudge zoom down.
            try {
                if (mapViewController?.fitBoundsForRoute) {
                    mapViewController.fitBoundsForRoute();
                }
                // Small delay to let fitBoundsForRoute settle before we set zoom
                setTimeout(() => {
                    try {
                        if (mapViewController?.setZoom) {
                            mapViewController.setZoom(NAV_ZOOM_LEVEL);
                        }
                    } catch (e) {
                        console.warn("[Pointr] setZoom failed:", e.message);
                    }
                }, 500);
            } catch (e) {
                console.warn("[Pointr] fitBoundsForRoute failed:", e.message);
            }

            console.log("[Pointr] Navigation started — beginning auto-advance");
            autoAdvanceSteps(navigationViewController, mapViewController, stepTimings);

        } catch (err) {
            console.error("[Pointr] navigation error:", err);
        }
    };

    // ── 4. Timer loop: calls startNextStep() per step, re-fits bounds each time ─
    const autoAdvanceSteps = (navigationViewController, mapViewController, steps) => {
        if (!steps?.length) return;

        let stepIndex = 0; // currently ON step 0 (startNavigation already showed it)

        const scheduleNext = () => {
            if (stepIndex >= steps.length) {
                console.log("[Pointr] All steps complete");
                return;
            }

            const currentDuration = steps[stepIndex].duration;

            stepTimerRef.current = setTimeout(() => {
                stepIndex += 1;
                if (stepIndex >= steps.length) {
                    console.log("[Pointr] Reached final step");
                    return;
                }

                try {
                    navigationViewController.startNextStep();
                    console.log(`[Pointr] Auto-advanced to step ${stepIndex + 1}`);

                    // Re-fit bounds after each step so cursor stays in view
                    setTimeout(() => {
                        try {
                            if (mapViewController?.fitBoundsForRoute) {
                                mapViewController.fitBoundsForRoute();
                            }
                            setTimeout(() => {
                                try {
                                    if (mapViewController?.setZoom) {
                                        mapViewController.setZoom(NAV_ZOOM_LEVEL);
                                    }
                                } catch (_) { }
                            }, 300);
                        } catch (_) { }
                    }, 200);

                } catch (e) {
                    console.warn("[Pointr] startNextStep() failed:", e);
                    return; // stop if SDK throws (e.g. already at destination)
                }

                scheduleNext();
            }, currentDuration);
        };

        scheduleNext();
    };

    // Cleanup step timers on unmount
    useEffect(() => {
        return () => {
            if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
        };
    }, []);

    // ── 5. When panel becomes visible → logo → map + start nav ───────────────
    useEffect(() => {

        console.log(`[BostonMap] isVisible changed: ${isVisible}`);
        if (!isVisible) {
            // Reset navigation state when becoming invisible
            navStartedRef.current = false;
            if (stepTimerRef.current) {
                clearTimeout(stepTimerRef.current);
                stepTimerRef.current = null;
            }
            // Reset phase to logo when becoming invisible (prepare for next visibility)
            setPhase("logo");
            return;
        }

        console.log("[BostonMap] Panel became visible — showing logo, will start map shortly");

        // Always reset phase to logo when becoming visible (for replay functionality)
        setPhase("logo");
        
        // Reset navigation state for fresh start
        navStartedRef.current = false;

        const logoTimer = setTimeout(() => {
            setPhase("map");

            if (sdkReadyRef.current && pointrWebRef.current && !navStartedRef.current) {
                const navCtrl = navCtrlRef.current
                    || pointrWebRef.current.getUiController().getNavigationViewController();
                const mapVC = mapVCRef.current
                    || pointrWebRef.current.getUiController().getMapViewController();
                startNavigation(pointrWebRef.current, navCtrl, mapVC);
            }
            // else: mapReady handler (first load) or initPointr (replay) will call startNavigation
        }, LOGO_DURATION);

        return () => {
            clearTimeout(logoTimer);
            // Clean up step timers when component unmounts or isVisible changes
            if (stepTimerRef.current) {
                clearTimeout(stepTimerRef.current);
                stepTimerRef.current = null;
            }
        };
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>

            {/* ── Logo overlay ─────────────────────────────────────────────────── */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    borderRadius: "15px",
                    zIndex: phase === "logo" ? 10 : -1,
                    opacity: phase === "logo" ? 1 : 0,
                    transition: "opacity 0.4s ease",
                    pointerEvents: "none",
                }}
            >
               <img
          src={logoBeforeMap?.fileUrl ? logoBeforeMap?.fileUrl : 'https://www.logoai.com/oss/icons/2021/10/27/MuCSnBxFpOQg2Kl.png'}
          alt="Loading..."
          style={{ width: logoBeforeMap?.width + '%' || '100%', height: logoBeforeMap?.width + '%' || '100%', objectFit: "contain" }}
        />
            </div>

            {/* ── Pointr map slot — persistent SDK container is appended here ─── */}
            {/* Do NOT give this div id="pointr-map-container"; the real container   */}
            {/* lives at module level and is re-attached on every mount.              */}
            <div
                ref={mapSlotRef}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "15px",
                    overflow: "hidden",
                    opacity: phase === "map" ? 1 : 0,
                    transition: "opacity 0.4s ease",
                }}
            />

            {/* ── Error state ───────────────────────────────────────────────────── */}
            {sdkError && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        color: "#e53e3e",
                        fontSize: "14px",
                        textAlign: "center",
                        padding: "20px",
                        borderRadius: "15px",
                        zIndex: 20,
                    }}
                >
                    ⚠️ Map could not load: {sdkError}
                </div>
            )}

        </div>
    );
};

export default BostonMap;